/**
 * offlineMetricsApi — fetch aggregated offline-evaluation metrics for the home
 * page from the backend (Smithy op GetOfflineEvalMetrics,
 * GET /usage-analytics/offline-eval-metrics). Replaces the old client-side
 * aggregation over listEvals(100): the backend now owns the window + the
 * promotion/integration split, with no 100-run cap.
 *
 * This issues a SigV4-signed GET against the Smithy service endpoint
 * (skillApiEndpoint) — the same auth chain and signing primitives the generated
 * client uses under the hood, and the same pattern EvalApiClient already uses
 * for the sibling eval-manager API. It is written by hand (rather than
 * getSkillClient().send(new GetOfflineEvalMetricsCommand(...))) because the
 * checked-in generated client package lags the model regeneration and does not
 * export that command yet; this works the moment the backend is deployed,
 * independent of when the client republishes. When the command does ship, this
 * can be swapped to the generated command with no change to callers.
 */

import { SignatureV4 } from "@smithy/signature-v4";
import { HttpRequest } from "@smithy/protocol-http";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { appConfig } from "../../../config";
import { getIdToken } from "../../../context/AuthContext";

/** Promotion-run aggregates (ordinary release-gate runs). */
export interface PromotionMetrics {
  total: number;
  evaluating: number;
  promoted: number;
  skipped: number;
  blocked: number;
  /** (promoted + skipped) / finished, as a percentage; null when nothing finished. */
  promotionRate: number | null;
  /** Average pass rate as a percentage over scored runs; 0 when none. */
  avgPassRate: number;
  scoredRuns: number;
}

/** Integration-test-run aggregates (binary pass/fail, no gate). */
export interface IntegrationMetrics {
  total: number;
  running: number;
  passed: number;
  failed: number;
  avgPassRate: number;
  scoredRuns: number;
}

export interface OfflineEvalMetrics {
  promotion: PromotionMetrics;
  integration: IntegrationMetrics;
  window: { startDate: string; endDate: string };
}

// The wire shape uses snake_case jsonName fields (see the Smithy model). Keep
// this internal so the exported shape stays camelCase for callers.
interface RawPromotion {
  total?: number;
  evaluating?: number;
  promoted?: number;
  skipped?: number;
  blocked?: number;
  promotion_rate?: number | null;
  avg_pass_rate?: number;
  scored_runs?: number;
}
interface RawIntegration {
  total?: number;
  running?: number;
  passed?: number;
  failed?: number;
  avg_pass_rate?: number;
  scored_runs?: number;
}
interface RawResponse {
  promotion?: RawPromotion;
  integration?: RawIntegration;
  window?: { start_date?: string; end_date?: string };
}

async function signedGet(path: string): Promise<RawResponse> {
  const idToken = getIdToken();
  if (!idToken) {
    throw new Error("[offlineMetricsApi] Not authenticated — no id_token available");
  }
  const credentials = await fromCognitoIdentityPool({
    identityPoolId: appConfig.identityPoolId,
    logins: { [appConfig.identityProvider]: idToken },
    clientConfig: { region: appConfig.region },
  })();

  const signer = new SignatureV4({
    service: "execute-api",
    region: appConfig.region,
    credentials,
    sha256: Sha256,
  });

  const url = new URL(`${appConfig.skillApiEndpoint}${path}`);
  const query: Record<string, string> = {};
  url.searchParams.forEach((v, k) => {
    query[k] = v;
  });

  const request = new HttpRequest({
    method: "GET",
    protocol: url.protocol,
    hostname: url.hostname,
    path: url.pathname,
    query,
    // Forward the Federate id_token so the backend can identify the caller and
    // check ldap_groups (per-user / eval-group authz). Set BEFORE signing so it
    // is covered by the SigV4 signature (tamper-proof) — mirrors IvtHubClient
    // (DAT-8256). SigV4 alone only proves some authenticated Cognito identity
    // (the shared authenticated role), not who.
    headers: { host: url.hostname, "X-Id-Token": idToken },
  });

  const signed = await signer.sign(request);
  const fetchHeaders: Record<string, string> = {};
  for (const [key, value] of Object.entries(signed.headers)) {
    if (key.toLowerCase() !== "host") fetchHeaders[key] = value as string;
  }
  const qs = new URLSearchParams(signed.query as Record<string, string>).toString();
  const fetchUrl = qs ? `${url.origin}${signed.path}?${qs}` : `${url.origin}${signed.path}`;

  const response = await fetch(fetchUrl, { method: "GET", headers: fetchHeaders });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(`[offlineMetricsApi] HTTP ${response.status}: ${text}`);
  }
  return response.json();
}

/** Normalize the snake_case wire response into the camelCase UI shape. Missing
 *  fields default to 0 / null so a partial response never throws. */
export function normalizeMetrics(raw: RawResponse): OfflineEvalMetrics {
  const p = raw.promotion ?? {};
  const i = raw.integration ?? {};
  return {
    promotion: {
      total: p.total ?? 0,
      evaluating: p.evaluating ?? 0,
      promoted: p.promoted ?? 0,
      skipped: p.skipped ?? 0,
      blocked: p.blocked ?? 0,
      promotionRate: p.promotion_rate ?? null,
      avgPassRate: p.avg_pass_rate ?? 0,
      scoredRuns: p.scored_runs ?? 0,
    },
    integration: {
      total: i.total ?? 0,
      running: i.running ?? 0,
      passed: i.passed ?? 0,
      failed: i.failed ?? 0,
      avgPassRate: i.avg_pass_rate ?? 0,
      scoredRuns: i.scored_runs ?? 0,
    },
    window: {
      startDate: raw.window?.start_date ?? "",
      endDate: raw.window?.end_date ?? "",
    },
  };
}

/**
 * Fetch offline-eval metrics for a period ("1d" / "7d" / "30d" / "90d").
 */
export async function getOfflineEvalMetrics(period: string): Promise<OfflineEvalMetrics> {
  const raw = await signedGet(
    `/usage-analytics/offline-eval-metrics?period=${encodeURIComponent(period)}`,
  );
  return normalizeMetrics(raw);
}
